/**
 * refreshToken — schedules a silent token refresh some buffer before the
 * id-token's `exp` claim. Cancellable via the returned controller.
 *
 * Story #2560 Sprint 6 Task #2576 (C04). Used by the cookie-driver pilot
 * (and any localStorage-driver consumer that wants automatic refresh).
 */

export interface RefreshTokenScheduleOptions {
  /** id-token JWT (or null/undefined to skip scheduling). */
  idToken: string | null | undefined;
  /** Async refresh callback; throw on failure. */
  refresh: () => Promise<void>;
  /** Buffer before exp to refresh. Default 300_000 (5 min). */
  bufferMs?: number;
  /** Optional callback when refresh fails. Default: console.warn. */
  onError?: (error: unknown) => void;
  /** Override Date.now for tests. */
  now?: () => number;
  /** Override setTimeout for tests. */
  setTimeoutFn?: (handler: () => void, ms: number) => unknown;
  /** Override clearTimeout for tests. */
  clearTimeoutFn?: (handle: unknown) => void;
}

export interface RefreshTokenController {
  /** Cancel any pending refresh. */
  cancel: () => void;
  /**
   * Resolved exp timestamp (ms since epoch) the refresh is scheduled
   * against, or null if no refresh was scheduled.
   */
  expiresAt: number | null;
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
};

export function scheduleTokenRefresh(
  options: RefreshTokenScheduleOptions,
): RefreshTokenController {
  const {
    idToken,
    refresh,
    bufferMs = 300_000,
    onError = (e) => console.warn('[shared-crm] refresh failed', e),
    now = Date.now,
    setTimeoutFn = setTimeout,
    clearTimeoutFn = clearTimeout,
  } = options;

  if (!idToken) {
    return { cancel: () => undefined, expiresAt: null };
  }

  const payload = decodeJwtPayload(idToken);
  const exp = payload && typeof payload.exp === 'number' ? payload.exp : null;
  if (!exp) {
    return { cancel: () => undefined, expiresAt: null };
  }

  const expiresAt = exp * 1000;
  const fireAt = expiresAt - bufferMs;
  const delay = fireAt - now();

  if (delay <= 0) {
    // Token is already past the refresh-window threshold. Fire immediately.
    void Promise.resolve()
      .then(() => refresh())
      .catch(onError);
    return { cancel: () => undefined, expiresAt };
  }

  const handle = setTimeoutFn(() => {
    Promise.resolve()
      .then(() => refresh())
      .catch(onError);
  }, delay);

  return {
    cancel: () => clearTimeoutFn(handle as never),
    expiresAt,
  };
}
